import React from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import ProjectAPI from "../../../../api/ProjectAPI";
import SimpleImageUpload from "../../../../components/utils/SimpleImageUpload";

import type { JSX } from "react";

interface IBackgroundImageSection {
  component: string;
}

function BackgroundImageSection(props: IBackgroundImageSection): JSX.Element {
  const { component } = props;
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { t } = useTranslation("editor-platform");

  const queryClient = useQueryClient();

  const getBackgroundImageQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getBackgroundImage,
    params: [
      {
        component,
        projectSlug,
      },
    ],
  });
  const {
    data: backgroundImage,
    isLoading: isLoadingGetBackground,
    error,
  } = useQuery({
    ...getBackgroundImageQuery,
    enabled: !!component,
  });

  if (error) {
    toast.error(t("get_background_image_error"));
  }

  const successMessage = (): void => {
    toast.success(t("save_background_image_success"));
  };

  const errorMessage = (): void => {
    toast.error(t("saveError"));
  };

  const deleteBackgroundImage = useMutation({
    mutationFn: async (componentName: string) =>
      ProjectAPI.deleteBackgroundImage({
        component: componentName,
        projectSlug,
      }),
    onSuccess: () => {
      queryClient.setQueryData(["backgroundImage", component], null);
      successMessage();
    },
    onError: () => errorMessage(),
  });

  const updateBackgroundImage = useMutation({
    mutationFn: async (data: { component: string; image: string }) =>
      ProjectAPI.updateBackgroundImage({ ...data, projectSlug }),
    onSuccess: (image: string) => {
      queryClient.setQueryData(["backgroundImage", component], image);
      successMessage();
    },
    onError: () => errorMessage(),
  });

  const handleChangeImage = (image: string): void => {
    updateBackgroundImage.mutate({
      component,
      image,
    });
  };

  const handleDeleteImage = (): void => {
    deleteBackgroundImage.mutate(component);
  };

  const isLoading =
    isLoadingGetBackground ||
    deleteBackgroundImage.isLoading ||
    updateBackgroundImage.isLoading;

  const title = (): string => {
    switch (component) {
      case "signup":
        return t("addSignupBackground");
      case "signin":
        return t("addSigninBackground");
      default:
        return t("addCustomBackground");
    }
  };

  return (
    <div className="editor-form-section">
      <div className="editor-form-section-title">{title()}</div>
      <div className="editor-form-row editor-form-row-inline">
        <SimpleImageUpload
          format="16_9"
          id={`background-${component}`}
          isLoading={isLoading}
          onDelete={(): void => handleDeleteImage()}
          onFileLoad={(image: string): void => handleChangeImage(image)}
          value={backgroundImage?.background_picture_url}
        />
      </div>
    </div>
  );
}

export default BackgroundImageSection;
